import React from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Trophy, Target, Zap, Users, TrendingUp } from 'lucide-react';

const STARStories = () => {
  const stories = [
    {
      icon: Trophy,
      title: '92% → 98% Audit Accuracy',
      color: 'border-l-4 border-blue-600 bg-white',
      situation: 'IAM platform had inconsistent entitlement data causing audit failures across Azure AD, Saviynt, and LDAP.',
      task: 'Find root cause and rebuild validation logic to bring audit accuracy to compliance targets.',
      action: 'Analyzed Snowflake tables and API payloads, rewrote SQL validation logic, fixed LDAP sync gaps, pushed through sprint as user stories with explicit acceptance criteria.',
      result: '92% → 98% audit accuracy on platform serving 2,000+ users. Directly protected regulatory compliance.',
    },
    {
      icon: Target,
      title: '100% On-Time — 4 Quarters',
      color: 'border-l-4 border-teal-600 bg-white',
      situation: 'Platform had recurring sprint delays — ambiguous requirements causing rework in UAT and missed goals.',
      task: 'Redesign how requirements were captured to eliminate ambiguity before development began.',
      action: 'Introduced structured user story templates with explicit acceptance criteria and edge cases. Pre-validated stories with engineers before sprint planning.',
      result: 'Req ambiguity –35%. 100% on-time delivery for four consecutive quarters. Zero UAT rework for 6 months.',
    },
    {
      icon: Zap,
      title: '40% Dashboard Latency Reduction',
      color: 'border-l-4 border-cyan-600 bg-white',
      situation: 'Power BI dashboards took 20+ minutes to refresh, slowing operational decisions for the compliance team.',
      task: 'Diagnose root cause and fix without rebuilding the entire Snowflake pipeline.',
      action: 'Traced issue to inefficient SQL joins and missing incremental load logic. Documented fix requirements and worked with the data engineer to rewrite the pipeline.',
      result: '22 min → 13 min refresh (40% improvement). Near real-time data for business stakeholders.',
    },
    {
      icon: TrendingUp,
      title: '30% Defect Reduction at Client B',
      color: 'border-l-4 border-indigo-600 bg-white',
      situation: 'High order defect rate across WMS fulfillment — mispicks, miscounts, and incorrect shipments at multiple facilities.',
      task: 'Identify where the data pipeline was introducing errors and fix without disrupting live operations.',
      action: 'Mapped the full order-to-delivery workflow, found 12 mismatched status codes and missing exception handling. Designed SQL validation checkpoints at each stage, tested in non-production first.',
      result: 'Order defects –30% · Inventory mismatches –25% · SLA breaches eliminated in 2 months.',
    },
    {
      icon: Users,
      title: 'Aligning Risk, Legal, Security & Technology',
      color: 'border-l-4 border-slate-600 bg-white',
      situation: 'A major platform upgrade required sign-off from Risk, Legal, Security, and Technology — all with different priorities and timelines, and a fixed sprint deadline.',
      task: 'Get alignment across all four groups without delaying the sprint delivery date.',
      action: 'Created a shared requirements doc mapping each team\'s concerns to specific acceptance criteria. Held individual structured walkthroughs with each group. Escalated one blocker to the product owner for prioritization.',
      result: 'All four teams signed off before the sprint began. Release deployed on time. Zero post-production issues. Approach adopted as team standard.',
    },
  ];

  return (
    <section id="stories" className="py-20 bg-slate-50">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800 border-blue-200 font-semibold">Interview Ready</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              STAR Stories
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl">
              Structured behavioral examples demonstrating problem-solving, delivery excellence, and stakeholder management.
            </p>
          </div>

          <div className="grid md:grid-cols-1 gap-6">
            {stories.map((story, index) => {
              const Icon = story.icon;
              return (
                <Card key={index} className={`${story.color} border-2 border-slate-200 hover:shadow-lg transition-all duration-300`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-slate-100 border-2 border-slate-300 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="text-slate-700" size={24} />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900">{story.title}</h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Situation</span>
                          <p className="text-sm text-slate-700">{story.situation}</p>
                        </div>
                        <div>
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Task</span>
                          <p className="text-sm text-slate-700">{story.task}</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Action</span>
                          <p className="text-sm text-slate-700">{story.action}</p>
                        </div>
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                          <span className="text-xs font-bold text-blue-700 uppercase tracking-wide block mb-2">Result</span>
                          <p className="text-sm text-slate-800 font-semibold">{story.result}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default STARStories;